import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Briefcase,
  AlertCircle,
  Loader2,
  Upload,
  Plus,
  Users,
  X,
  FileText,
  Check,
  ChevronDown,
  Ban,
  Undo2,
  Share2,
  Layers,
  Calendar,
  Mail,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  useAuth,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  MultiSelectCombobox,
  FilterCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
  toast,
} from '@aaas/shared';

function getApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
    if (detail) return detail;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
import { useQueryClient } from '@tanstack/react-query';
import { useJobKanbanQuery, useJobAssignmentsQuery } from '../../hooks/useJobQueries';
import { usePoolListQuery } from '../../hooks/usePoolQueries';
import { useATSRole } from '../../hooks/useATSRole';
import { useSourceListQuery } from '../../hooks/useSourceQueries';
import { useReferrerListQuery } from '../../hooks/useReferrerQueries';
import referrerService from '../../services/referrer.service';
import { KanbanBoard } from '../../components/KanbanBoard';
import { ShareCVsDialog } from '../../components/ShareCVsDialog';
import { RejectReasonModal } from '../../components/RejectReasonModal';
import { SelectFromPoolModal } from '../../components/job/SelectFromPoolModal';
import jobService from '../../services/job.service';
import cvService from '../../services/cv.service';
import { JOB_STATUS_CONFIG, BULK_CV_LIMIT, type JobStatus, type JobDetail, type JobCVCard } from '../../types';
import { AddCVToPoolDialog } from '../../components/AddCVToPoolDialog';
import { SendEmailDialog } from '../../components/SendEmailDialog';
import { JobJDPanel } from '../../components/JobJDPanel';
import { JDUploadDialog } from '../../components/JDUploadDialog';
import { JobMatchingPanel } from '../../components/matching/JobMatchingPanel';
import { CalendarEventFormDialog } from '../../components/CalendarEventFormDialog';

// Status badge component
function StatusBadge({ status }: { status: JobStatus }) {
  const config = JOB_STATUS_CONFIG[status];
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-100 text-gray-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };

  const dotClasses = {
    green: 'bg-green-500',
    gray: 'bg-gray-500',
    yellow: 'bg-yellow-500',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotClasses[config.color]}`} />
      {config.label}
    </span>
  );
}

// Upload file status type
interface UploadFileStatus {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export function JobDetailPage() {
  const { t } = useTranslation('ats');
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, currentWorkspace, idToken } = useAuth();
  const [isMoving, setIsMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [hideRejected, setHideRejected] = useState(() => {
    const param = searchParams.get('hideRejected');
    return param === null ? true : param === 'true';
  });
  const [showOnlyMyPic, setShowOnlyMyPic] = useState(() => {
    const param = searchParams.get('showOnlyMyPic');
    return param === null ? true : param === 'true';
  });

  // Bulk selection state
  const [selectedCVIds, setSelectedCVIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Add to pool dialog state (bulk)
  const [showAddToPoolDialog, setShowAddToPoolDialog] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadFileStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>();
  const [selectedPicId, setSelectedPicId] = useState<string | undefined>();
  const [selectedReferrerId, setSelectedReferrerId] = useState<string | undefined>();
  const modalFileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Select from pool modal state
  const [showSelectFromPoolModal, setShowSelectFromPoolModal] = useState(false);

  // Calendar event flow state
  const [calendarEventCvId, setCalendarEventCvId] = useState<string | undefined>();
  const [showCalendarEventFormDialog, setShowCalendarEventFormDialog] = useState(false);

  // Fetch current user's role
  const { isRecruiter, isLeaderOrAbove, currentUserAccountId } = useATSRole();

  // CV / JD / Matching view toggle
  const [activeView, setActiveView] = useState<'cv' | 'jd' | 'matching'>('cv');
  const [matchingSubView, setMatchingSubView] = useState<'history' | 'create'>('history');
  const [showJDUploadDialog, setShowJDUploadDialog] = useState(false);
  // Add referrer dialog state
  const [showAddReferrerDialog, setShowAddReferrerDialog] = useState(false);
  const [newReferrerName, setNewReferrerName] = useState('');
  const [newReferrerEmail, setNewReferrerEmail] = useState('');
  const [isCreatingReferrer, setIsCreatingReferrer] = useState(false);
  const [referrerError, setReferrerError] = useState<string | null>(null);

  const {
    data: job,
    isLoading: loading,
    error,
  } = useJobKanbanQuery({
    jobId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!jobId,
    refetchInterval: activeView === 'cv' ? 5000 : false, // only poll kanban in CV view
  });

  // Fetch pools (for upload modal)
  const { data: poolsData } = usePoolListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: showUploadModal && !!currentWorkspace,
    refetchInterval: false,
  });

  // Fetch sources (for upload modal)
  const { data: sourcesData } = useSourceListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: showUploadModal && !!currentWorkspace,
    refetchInterval: false,
  });

  // Fetch referrers (for upload modal)
  const { data: referrersData } = useReferrerListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    size: 100,
    enabled: showUploadModal && !!currentWorkspace,
    refetchInterval: false,
  });

  // Fetch job assignees for PIC options
  const { data: assignmentsData } = useJobAssignmentsQuery({
    jobId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: showUploadModal && !!currentWorkspace && !!jobId,
  });

  const pools = poolsData?.data?.items || [];
  const sources = sourcesData?.data?.items || [];
  const referrers = referrersData?.data?.items || [];
  const picOptions = useMemo(() => {
    if (isRecruiter && currentUserAccountId && user) {
      return [{ value: currentUserAccountId, label: user.displayName || user.email || currentUserAccountId }];
    }
    return (
      assignmentsData?.items.map((a) => ({
        value: a.assignee.id,
        label: a.assignee.display_name || a.assignee.email || a.assignee.id,
      })) ?? []
    );
  }, [isRecruiter, currentUserAccountId, user, assignmentsData]);

  const handleBack = () => {
    navigate('/ats/jobs');
  };

  const getCVNameById = useCallback((cvId: string) => {
    if (!job) return cvId;
    for (const col of job.columns) {
      const cv = col.cvs.find((c) => c.id === cvId);
      if (cv) return cv.name;
    }
    return cvId;
  }, [job]);

  const handleToggleHideRejected = useCallback(() => {
    const newValue = !hideRejected;
    setHideRejected(newValue);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('hideRejected', String(newValue));
      return newParams;
    });
  }, [hideRejected, setSearchParams]);

  const handleToggleShowOnlyMyPic = useCallback(() => {
    const newValue = !showOnlyMyPic;
    setShowOnlyMyPic(newValue);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('showOnlyMyPic', String(newValue));
      return newParams;
    });
  }, [showOnlyMyPic, setSearchParams]);

  const handleMoveCV = useCallback(
    async (cvId: string, fromColumnId: string, toColumnId: string) => {
      if (!user || !currentWorkspace || !jobId || !job) return;

      setMoveError(null);
      setIsMoving(true);

      // Optimistic update
      const queryKey = ['job-kanban', jobId, currentWorkspace.id];
      const previousData = queryClient.getQueryData<JobDetail>(queryKey);

      if (previousData) {
        // Create updated columns
        const newColumns = previousData.columns.map((col) => {
          const colId = col.id;
          const fromId = fromColumnId;
          const toId = toColumnId;

          // Remove CV from source column
          if (colId === fromId) {
            return {
              ...col,
              cvs: col.cvs.filter((cv) => cv.id !== cvId),
            };
          }

          // Add CV to target column
          if (colId === toId) {
            const movedCV = previousData.columns
              .find((c) => c.id === fromId)
              ?.cvs.find((cv) => cv.id === cvId);

            if (movedCV) {
              return {
                ...col,
                cvs: [...col.cvs, movedCV],
              };
            }
          }

          return col;
        });

        queryClient.setQueryData(queryKey, {
          ...previousData,
          columns: newColumns,
        });
      }

      try {
        const token = await user.getIdToken();
        await jobService.updateCVRound(jobId, cvId, currentWorkspace.id, token, toColumnId);
        // Success - optimistic update is already applied
      } catch (err) {
        // Rollback on error
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
        toast.error(getApiError(err, t('jobDetail.actions.moveFailed')));
        console.error('Error moving CV:', err);
      } finally {
        setIsMoving(false);
      }
    },
    [user, currentWorkspace, jobId, job, queryClient, t]
  );

  // Handle file selection (from input or drop)
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles: UploadFileStatus[] = [];
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (allowedTypes.includes(file.type)) {
        validFiles.push({ file, status: 'pending' });
      }
    }

    if (validFiles.length > 0) {
      setUploadingFiles((prev) => [...prev, ...validFiles]);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragActive(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Remove file from list
  const removeFile = useCallback((index: number) => {
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Upload all files
  const handleUploadAll = useCallback(async () => {
    if (!user || !currentWorkspace || !jobId || uploadingFiles.length === 0) return;

    setIsUploading(true);

    try {
      const token = await user.getIdToken();

      for (let i = 0; i < uploadingFiles.length; i++) {
        const fileStatus = uploadingFiles[i];
        if (fileStatus.status !== 'pending') continue;

        // Update status to uploading
        setUploadingFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading' } : f))
        );

        try {
          await jobService.uploadCVToJob(
            jobId,
            currentWorkspace.id,
            token,
            fileStatus.file,
            selectedPoolIds.length > 0 ? selectedPoolIds : undefined,
            selectedSourceId,
            selectedPicId,
            selectedReferrerId
          );

          // Update status to done
          setUploadingFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'done' } : f))
          );
        } catch (err) {
          // Update status to error
          const errorMessage = err instanceof Error ? err.message : t('jobDetail.actions.uploadFailed');
          setUploadingFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: errorMessage } : f))
          );
        }
      }

      // Refetch Kanban data
      queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace.id] });
    } finally {
      setIsUploading(false);
    }
  }, [
    user,
    currentWorkspace,
    jobId,
    uploadingFiles,
    selectedPoolIds,
    selectedSourceId,
    selectedPicId,
    selectedReferrerId,
    queryClient,
    t,
  ]);

  // Close modal and reset
  const handleCloseModal = useCallback(() => {
    setShowUploadModal(false);
    setUploadingFiles([]);
    setSelectedPoolIds([]);
    setSelectedSourceId(undefined);
    setSelectedPicId(undefined);
    setSelectedReferrerId(undefined);
    setIsDragActive(false);
    dragCounterRef.current = 0;
  }, []);

  // Handle create new referrer
  const handleCreateReferrer = useCallback(async () => {
    if (!user || !currentWorkspace || !newReferrerName || !newReferrerEmail) return;

    setIsCreatingReferrer(true);
    setReferrerError(null);

    try {
      const token = await user.getIdToken();
      const newReferrer = await referrerService.createReferrer(
        currentWorkspace.id,
        token,
        newReferrerName.trim(),
        newReferrerEmail.trim()
      );

      // Invalidate and refetch referrers list
      queryClient.invalidateQueries({ queryKey: ['referrers', currentWorkspace.id] });

      // Auto-select the newly created referrer
      setSelectedReferrerId(newReferrer.id);

      // Close dialog and reset form
      setShowAddReferrerDialog(false);
      setNewReferrerName('');
      setNewReferrerEmail('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('jobDetail.actions.createReferrerFailed');
      setReferrerError(errorMessage);
      console.error('Error creating referrer:', err);
    } finally {
      setIsCreatingReferrer(false);
    }
  }, [user, currentWorkspace, newReferrerName, newReferrerEmail, queryClient, t]);

  // Bulk selection handlers
  const toggleSelectCV = useCallback((cvId: string) => {
    setSelectedCVIds((prev) => {
      const next = new Set(prev);
      if (next.has(cvId)) {
        next.delete(cvId);
      } else {
        next.add(cvId);
      }
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedCVIds(new Set());
  }, []);

  // Bulk move CVs via drag and drop
  const handleBulkMoveCV = useCallback(
    async (cvIds: string[], fromColumnId: string, toColumnId: string) => {
      if (!user || !currentWorkspace || !jobId || !job || cvIds.length === 0) return;

      setMoveError(null);
      setIsMoving(true);

      // Optimistic update
      const queryKey = ['job-kanban', jobId, currentWorkspace.id];
      const previousData = queryClient.getQueryData<JobDetail>(queryKey);

      if (previousData) {
        const cvIdsSet = new Set(cvIds);
        const newColumns = previousData.columns.map((col) => {
          const colId = col.id;

          // Remove CVs from source column
          if (colId === fromColumnId) {
            return {
              ...col,
              cvs: col.cvs.filter((cv) => !cvIdsSet.has(cv.id)),
            };
          }

          // Add CVs to target column
          if (colId === toColumnId) {
            const movedCVs = previousData.columns
              .find((c) => c.id === fromColumnId)
              ?.cvs.filter((cv) => cvIdsSet.has(cv.id)) || [];

            return {
              ...col,
              cvs: [...col.cvs, ...movedCVs],
            };
          }

          return col;
        });

        queryClient.setQueryData(queryKey, {
          ...previousData,
          columns: newColumns,
        });
      }

      try {
        const token = await user.getIdToken();
        await jobService.bulkUpdateCVJobs(jobId, currentWorkspace.id, token, {
          cv_ids: cvIds,
          round_id: toColumnId,
        });
        // Success - clear selection and optimistic update is already applied
        deselectAll();
      } catch (err) {
        // Rollback on error
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
        toast.error(getApiError(err, t('jobDetail.actions.moveBulkFailed')));
        console.error('Error bulk moving CVs:', err);
      } finally {
        setIsMoving(false);
      }
    },
    [user, currentWorkspace, jobId, job, queryClient, deselectAll, t]
  );

  // Bulk move to round
  const handleBulkMoveToRound = useCallback(
    async (roundId: string) => {
      if (!user || !currentWorkspace || !jobId || selectedCVIds.size === 0) return;

      setIsBulkProcessing(true);
      try {
        const token = await user.getIdToken();

        await jobService.bulkUpdateCVJobs(jobId, currentWorkspace.id, token, {
          cv_ids: Array.from(selectedCVIds),
          round_id: roundId,
        });

        queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace.id] });
        deselectAll();
      } catch (err) {
        toast.error(getApiError(err, t('jobDetail.actions.moveBulkFailed')));
        console.error('Error bulk moving CVs:', err);
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [user, currentWorkspace, jobId, selectedCVIds, queryClient, deselectAll, t]
  );

  // Bulk reject (with reason)
  const handleConfirmReject = useCallback(async (reasonId: string, customText: string) => {
    if (!user || !currentWorkspace || !jobId || selectedCVIds.size === 0) {
      throw new Error(t('jobDetail.actions.missingData'));
    }

    setIsBulkProcessing(true);
    try {
      const token = await user.getIdToken();

      await jobService.bulkUpdateCVJobs(jobId, currentWorkspace.id, token, {
        cv_ids: Array.from(selectedCVIds),
        is_rejected: true,
        rejection_reason_id: reasonId,
        rejection_reason_text: customText || null,
      });

      queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace.id] });
      deselectAll();
      setShowRejectModal(false);
    } catch (err) {
      toast.error(getApiError(err, t('jobDetail.actions.rejectBulkFailed')));
      console.error('Error bulk rejecting CVs:', err);
      throw err; // Re-throw to let modal handle the error state
    } finally {
      setIsBulkProcessing(false);
    }
  }, [
    user,
    currentWorkspace,
    jobId,
    selectedCVIds,
    queryClient,
    deselectAll,
    t,
  ]);

  // Bulk unreject
  const handleBulkUnreject = useCallback(async () => {
    if (!user || !currentWorkspace || !jobId || selectedCVIds.size === 0) return;

    setIsBulkProcessing(true);
    try {
      const token = await user.getIdToken();

      await jobService.bulkUpdateCVJobs(jobId, currentWorkspace.id, token, {
        cv_ids: Array.from(selectedCVIds),
        is_rejected: false,
      });

      queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace.id] });
      deselectAll();
    } catch (err) {
      toast.error(getApiError(err, t('jobDetail.actions.unrejectBulkFailed')));
      console.error('Error bulk unrejecting CVs:', err);
    } finally {
      setIsBulkProcessing(false);
    }
  }, [user, currentWorkspace, jobId, selectedCVIds, queryClient, deselectAll, t]);

  // Bulk add to pools
  const handleBulkAddToPools = useCallback(
    async (poolIds: string[]) => {
      if (!user || !currentWorkspace || selectedCVIds.size === 0) {
        throw new Error(t('jobDetail.actions.missingData'));
      }
      if (selectedCVIds.size > BULK_CV_LIMIT) {
        const msg = t('jobDetail.actions.bulkLimit', { limit: BULK_CV_LIMIT, selected: selectedCVIds.size });
        setMoveError(msg);
        throw new Error(msg);
      }

      setIsBulkProcessing(true);
      try {
        const token = await user.getIdToken();
        await cvService.bulkAddCVsToPools(
          Array.from(selectedCVIds),
          currentWorkspace.id,
          token,
          poolIds
        );
        queryClient.invalidateQueries({ queryKey: ['cvs'] });
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('jobDetail.actions.addToPoolsFailed');
        setMoveError(msg);
        throw err;
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [user, currentWorkspace, selectedCVIds, queryClient, t]
  );

  // Filter CVs based on role and toggles
  const filteredColumns = useMemo(() => {
    if (!job) return [];

    return job.columns.map((col) => ({
      ...col,
      cvs: col.cvs.filter((cv) => {
        // Filter rejected CVs if toggle is on
        if (hideRejected && cv.is_rejected) return false;

        // Filter to only show CVs where current user is PIC (Recruiter only)
        // Guard with isRecruiter and currentUserAccountId to avoid filtering during loading
        // or affecting managers/leaders who may not be PIC of every CV.
        if (
          isRecruiter &&
          showOnlyMyPic &&
          currentUserAccountId &&
          cv.person_in_charge_id !== currentUserAccountId
        )
          return false;

        return true;
      }),
    }));
  }, [job, hideRejected, isRecruiter, showOnlyMyPic, currentUserAccountId]);

  // When showOnlyMyPic=false and user is Recruiter, dim CVs they are not PIC of
  // Guard on currentUserAccountId to avoid dimming all CVs while it is still loading
  const getIsDisabled = useMemo<((cv: JobCVCard) => boolean) | undefined>(() => {
    if (!isRecruiter || showOnlyMyPic || !currentUserAccountId) return undefined;
    return (cv: JobCVCard) => cv.person_in_charge_id !== currentUserAccountId;
  }, [isRecruiter, showOnlyMyPic, currentUserAccountId]);

  // Select/deselect all CVs in a specific round
  const toggleSelectAllInRound = useCallback(
    (roundId: string) => {
      if (!job) return;

      const column = filteredColumns.find((col) => col.id === roundId);
      if (!column || column.cvs.length === 0) return;

      // Only include selectable (non-disabled) CVs
      const selectableCVs = getIsDisabled
        ? column.cvs.filter((cv) => !getIsDisabled(cv))
        : column.cvs;
      if (selectableCVs.length === 0) return;

      const cvIdsInRound = new Set(selectableCVs.map((cv) => cv.id));
      const allSelected = Array.from(cvIdsInRound).every((id) => selectedCVIds.has(id));

      setSelectedCVIds((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          // Deselect all in this round
          cvIdsInRound.forEach((id) => next.delete(id));
        } else {
          // Select all in this round
          cvIdsInRound.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [job, filteredColumns, selectedCVIds, getIsDisabled]
  );

  // Check if there are pending files
  const hasPendingFiles = uploadingFiles.some((f) => f.status === 'pending');
  const allDone = uploadingFiles.length > 0 && uploadingFiles.every((f) => f.status === 'done');

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('jobDetail.backToJobs')}
        </button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('jobDetail.actions.loadJobFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!job || !jobId) {
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - compact single row */}
      <div className="flex-shrink-0 px-4 py-3 flex items-center gap-4 border-b border-gray-200">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="h-4 w-px bg-gray-300" />

        {/* Job info */}
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-orange-600" />
          <span className="font-bold text-gray-900 font-mono">{job.code}</span>
          <StatusBadge status={job.status} />
          {isMoving && <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />}
        </div>

        <div className="h-4 w-px bg-gray-300" />

        {/* Position name */}
        <span className="text-gray-700 truncate max-w-xs">{job.position.name}</span>

        {isLeaderOrAbove && <div className="h-4 w-px bg-gray-300" />}

        {/* View toggle: CV | JD — hidden for recruiters */}
        {isLeaderOrAbove && (
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => setActiveView('cv')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeView === 'cv' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              CV
            </button>
            <button
              onClick={() => setActiveView('jd')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${activeView === 'jd' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              JD
            </button>
          </div>
        )}

        {/* Divider before Matching button */}
        {isLeaderOrAbove && <div className="h-4 w-px bg-gray-300" />}

        {/* Matching — separate icon button, hidden for recruiters */}
        {isLeaderOrAbove && (
          <button
            onClick={() => setActiveView(activeView === 'matching' ? 'cv' : 'matching')}
            title={t('jobDetail.matching')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
              activeView === 'matching'
                ? 'bg-orange-600 text-white border-orange-600'
                : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Layers className="h-3.5 w-3.5" />
            {t('jobDetail.matching')}
          </button>
        )}

        {/* History | Create — only when matching is active */}
        {isLeaderOrAbove && activeView === 'matching' && (
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => setMatchingSubView('history')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                matchingSubView === 'history' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('jobDetail.history')}
            </button>
            <button
              onClick={() => setMatchingSubView('create')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${
                matchingSubView === 'create' ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('jobDetail.create')}
            </button>
          </div>
        )}

        {activeView === 'jd' && isLeaderOrAbove && (
          <>
            <div className="h-4 w-px bg-gray-300" />
            <button
              onClick={() => setShowJDUploadDialog(true)}
              className="inline-flex items-center justify-center w-8 h-8 text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>
          </>
        )}

        {activeView === 'cv' && (
          <>
            <div className="h-4 w-px bg-gray-300" />

            {/* Hide rejected toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-gray-600">{t('jobDetail.hideRejected')}</span>
              <button
                type="button"
                role="switch"
                aria-checked={hideRejected}
                onClick={handleToggleHideRejected}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  hideRejected ? 'bg-orange-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hideRejected ? 'translate-x-[18px]' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </label>

            {/* PIC filter toggle — Recruiter only */}
            {isRecruiter && (
              <>
                <div className="h-4 w-px bg-gray-300" />
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm text-gray-600">{t('jobDetail.myCvs')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showOnlyMyPic}
                    onClick={handleToggleShowOnlyMyPic}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      showOnlyMyPic ? 'bg-orange-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showOnlyMyPic ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </label>
              </>
            )}

            <div className="h-4 w-px bg-gray-300" />

            {/* Add CV dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center justify-center w-8 h-8 text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors">
                  <Plus className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setShowSelectFromPoolModal(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  {t('jobDetail.selectFromPool')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                    if (isRecruiter && currentUserAccountId) setSelectedPicId(currentUserAccountId);
                    setShowUploadModal(true);
                  }}>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('jobDetail.uploadCv')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </>
        )}


        {/* Spacer */}
        <div className="flex-1" />

        {/* Metadata - right aligned */}
        <div className="hidden md:flex items-center gap-4 text-sm text-gray-500">
          <span>{job.campaign.name}</span>
          <span>•</span>
          <span>{job.pipeline?.name || t('jobDetail.noPipeline')}</span>
          <span>•</span>
          <span>{t('jobDetail.qty', { count: job.quantity })}</span>
        </div>
      </div>

      {/* Error messages - if any */}
      {moveError && (
        <div className="px-4 py-2">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{moveError}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main content: CV kanban | JD panel | Matching panel */}
      <div className={`flex-1 min-h-0 ${activeView === 'cv' || !isLeaderOrAbove ? 'px-4 py-2' : ''}`}>
        {activeView === 'cv' || !isLeaderOrAbove ? (
          <KanbanBoard
            columns={filteredColumns}
            jobId={jobId}
            onMoveCV={handleMoveCV}
            onBulkMoveCV={handleBulkMoveCV}
            selectedCVIds={selectedCVIds}
            onToggleSelect={toggleSelectCV}
            onToggleSelectAllInRound={toggleSelectAllInRound}
            getIsDisabled={getIsDisabled}
          />
        ) : activeView === 'jd' ? (
          <JobJDPanel jobId={jobId} />
        ) : (
          <JobMatchingPanel
            jobId={jobId}
            cvCards={job.columns.flatMap((col) => col.cvs)}
            isCVsLoading={loading}
            subView={matchingSubView}
          />
        )}
      </div>

      {/* Upload CV Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('jobDetail.upload.title')}</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-4 space-y-4">
              {/* Drag-drop zone */}
              <div
                onClick={() => modalFileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-orange-600 bg-orange-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  ref={modalFileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    handleFileSelect(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                {isDragActive ? (
                  <p className="text-orange-600 font-medium">{t('jobDetail.upload.dropHere')}</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-1">
                      {t('jobDetail.upload.dragDropPrefix')}{' '}
                      <span className="text-orange-600">{t('jobDetail.upload.clickToSelect')}</span>
                    </p>
                    <p className="text-xs text-gray-400">{t('jobDetail.upload.supportedFormats')}</p>
                  </div>
                )}
              </div>

              {/* File list */}
              {uploadingFiles.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {uploadingFiles.map((fileStatus, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-700 truncate">
                        {fileStatus.file.name}
                      </span>
                      {fileStatus.status === 'pending' && (
                        <button
                          onClick={() => removeFile(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      {fileStatus.status === 'uploading' && (
                        <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
                      )}
                      {fileStatus.status === 'done' && <Check className="h-4 w-4 text-green-600" />}
                      {fileStatus.status === 'error' && (
                        <span className="text-xs text-red-500">{fileStatus.error || t('jobDetail.upload.errorLabel')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pool selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('jobDetail.upload.addToPools')}
                </label>
                <div className="w-full">
                  <MultiSelectCombobox
                    options={pools.map((pool) => ({ value: pool.id, label: pool.name }))}
                    values={selectedPoolIds}
                    onChange={setSelectedPoolIds}
                    placeholder={t('jobDetail.upload.selectPools')}
                    displayName={t('jobDetail.upload.poolsName')}
                    searchPlaceholder={t('jobDetail.upload.searchPools')}
                    emptyMessage={t('jobDetail.upload.noPoolsFound')}
                    isLoading={false}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Source selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('jobDetail.upload.cvSource')}
                </label>
                <FilterCombobox
                  options={sources.map((source) => ({ value: source.id, label: source.name }))}
                  value={selectedSourceId}
                  onChange={setSelectedSourceId}
                  placeholder={t('jobDetail.upload.selectCvSource')}
                  searchPlaceholder={t('jobDetail.upload.searchSources')}
                  emptyMessage={t('jobDetail.upload.noSourcesFound')}
                  className='w-full'
                />
              </div>

              {/* PIC selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('jobDetail.upload.responsiblePerson')}
                </label>
                {isRecruiter ? (
                  <Select value={selectedPicId}>
                    <SelectTrigger className="w-full" disabled>
                      <SelectValue placeholder={t('jobDetail.upload.selectResponsible')}/>
                    </SelectTrigger>
                    <SelectContent>
                      {picOptions
                        .filter((o) => o.value === currentUserAccountId)
                        .map((o) => (
                          <SelectItem key={o.value || ''} value={o.value || ''}>
                            {o.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <FilterCombobox
                    options={picOptions}
                    value={selectedPicId}
                    onChange={setSelectedPicId}
                    placeholder={t('jobDetail.upload.selectResponsible')}
                    searchPlaceholder={t('jobDetail.upload.searchPeople')}
                    emptyMessage={t('jobDetail.upload.noMatchFound')}
                    className="w-full"
                  />
                )}
              </div>

              {/* Referrer selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('jobDetail.upload.referrer')}
                  </label>
                  {isLeaderOrAbove && (
                    <button
                      type="button"
                      onClick={() => setShowAddReferrerDialog(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      {t('jobDetail.upload.addNew')}
                    </button>
                  )}
                </div>
                <FilterCombobox
                  options={referrers.map((ref) => ({ value: ref.id, label: `${ref.name} (${ref.email})` }))}
                  value={selectedReferrerId}
                  onChange={setSelectedReferrerId}
                  placeholder={t('jobDetail.upload.selectReferrer')}
                  searchPlaceholder={t('jobDetail.upload.searchPeople')}
                  emptyMessage={t('jobDetail.upload.noMatchFound')}
                  className='w-full'
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {allDone ? t('jobDetail.upload.close') : t('jobDetail.upload.cancel')}
              </button>
              {!allDone && (
                <button
                  onClick={handleUploadAll}
                  disabled={!hasPendingFiles || isUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('jobDetail.upload.uploading')}
                    </span>
                  ) : (
                    t('jobDetail.upload.uploadCount', { count: uploadingFiles.filter((f) => f.status === 'pending').length })
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Select from Pool Modal */}
      <SelectFromPoolModal
        open={showSelectFromPoolModal}
        onClose={() => setShowSelectFromPoolModal(false)}
        jobId={jobId}
        jobCode={job.code}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace?.id] });
        }}
      />

      {/* Bulk Action Bar */}
      {selectedCVIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3 flex items-center gap-4">
          {/* Count */}
          <span className="text-gray-700">
            {t('jobDetail.bulk.candidatesSelected', { count: selectedCVIds.size })}
          </span>

          {/* Change round dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={isBulkProcessing}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
              >
                {t('jobDetail.bulk.changeRound')}
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {job.columns.map((col) => (
                <DropdownMenuItem key={col.id} onClick={() => handleBulkMoveToRound(col.id)}>
                  {col.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={isBulkProcessing}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors disabled:opacity-50"
              >
                {t('jobDetail.bulk.actions')}
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {isLeaderOrAbove && (
                <DropdownMenuItem onClick={() => setShowShareDialog(true)}>
                  <Share2 className="h-4 w-4 mr-2 text-blue-500" />
                  {t('jobDetail.bulk.share')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowAddToPoolDialog(true)}>
                <Layers className="h-4 w-4 mr-2 text-purple-500" />
                {t('jobDetail.bulk.addToPool')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowSendEmailDialog(true)}>
                <Mail className="h-4 w-4 mr-2 text-orange-500" />
                {t('jobDetail.bulk.sendEmail')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowRejectModal(true)}>
                <Ban className="h-4 w-4 mr-2 text-red-500" />
                {t('jobDetail.bulk.rejectCandidate')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleBulkUnreject}>
                <Undo2 className="h-4 w-4 mr-2 text-green-500" />
                {t('jobDetail.bulk.unreject')}
              </DropdownMenuItem>
              {selectedCVIds.size === 1 && (
                <DropdownMenuItem onClick={() => {
                  setCalendarEventCvId(Array.from(selectedCVIds)[0]);
                  setShowCalendarEventFormDialog(true);
                }}>
                  <Calendar className="h-4 w-4 mr-2 text-orange-500" />
                  {t('jobDetail.bulk.scheduleInterview')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Close button */}
          <button
            onClick={deselectAll}
            disabled={isBulkProcessing}
            className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-md transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Loading indicator */}
          {isBulkProcessing && <Loader2 className="h-5 w-5 text-green-600 animate-spin" />}
        </div>
      )}

      {/* Reject Reason Modal */}
      <RejectReasonModal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleConfirmReject}
        description={t('jobDetail.bulk.rejectReasonDescription', { count: selectedCVIds.size })}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
      />

      {/* Share CVs Dialog */}
      {showShareDialog && jobId && (
        <ShareCVsDialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          jobId={jobId}
          selectedCVIds={Array.from(selectedCVIds)}
        />
      )}

      {/* Send Email Dialog */}
      <SendEmailDialog
        open={showSendEmailDialog}
        onClose={() => {
          setShowSendEmailDialog(false);
          deselectAll();
        }}
        cvIds={Array.from(selectedCVIds)}
        jobId={jobId}
      />

      {/* Add Referrer Dialog */}
      {showAddReferrerDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Dialog header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('jobDetail.referrer.title')}</h3>
              <button
                onClick={() => {
                  setShowAddReferrerDialog(false);
                  setNewReferrerName('');
                  setNewReferrerEmail('');
                  setReferrerError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Dialog body */}
            <div className="px-6 py-4 space-y-4">
              {referrerError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{referrerError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="referrer-name">{t('jobDetail.referrer.fullName')}</Label>
                <Input
                  id="referrer-name"
                  type="text"
                  value={newReferrerName}
                  onChange={(e) => setNewReferrerName(e.target.value)}
                  placeholder={t('jobDetail.referrer.fullNamePlaceholder')}
                  disabled={isCreatingReferrer}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referrer-email">{t('jobDetail.referrer.email')}</Label>
                <Input
                  id="referrer-email"
                  type="email"
                  value={newReferrerEmail}
                  onChange={(e) => setNewReferrerEmail(e.target.value)}
                  placeholder={t('jobDetail.referrer.emailPlaceholder')}
                  disabled={isCreatingReferrer}
                />
              </div>
            </div>

            {/* Dialog footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddReferrerDialog(false);
                  setNewReferrerName('');
                  setNewReferrerEmail('');
                  setReferrerError(null);
                }}
                disabled={isCreatingReferrer}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
              >
                {t('jobDetail.referrer.cancel')}
              </button>
              <button
                onClick={handleCreateReferrer}
                disabled={isCreatingReferrer || !newReferrerName.trim() || !newReferrerEmail.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingReferrer ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('jobDetail.referrer.creating')}
                  </span>
                ) : (
                  t('jobDetail.referrer.create')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Pool Dialog (bulk) */}
      <AddCVToPoolDialog
        open={showAddToPoolDialog}
        onClose={() => setShowAddToPoolDialog(false)}
        onConfirm={handleBulkAddToPools}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
        title={t('jobDetail.bulk.addToPoolTitle', { count: selectedCVIds.size })}
      />

      {/* JD Upload Dialog */}
      {jobId && (
        <JDUploadDialog
          open={showJDUploadDialog}
          onClose={() => setShowJDUploadDialog(false)}
          jobId={jobId}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['job-descriptions-all', currentWorkspace?.id] });
            setShowJDUploadDialog(false);
          }}
        />
      )}

      {/* Calendar Event Form */}
      <CalendarEventFormDialog
        event={null}
        open={showCalendarEventFormDialog}
        onOpenChange={(open) => {
          setShowCalendarEventFormDialog(open);
          if (!open) setCalendarEventCvId(undefined);
        }}
        cvId={calendarEventCvId}
        cvName={calendarEventCvId ? getCVNameById(calendarEventCvId) : undefined}
        jobId={jobId}
      />
    </div>
  );
}
